<template>
  <div class="upload-section">
    <div class="upload-header">
      <q-icon name="upload_file" size="24px" class="header-icon" />
      <h6 class="upload-title">{{ titulo }}</h6>
    </div>

    <div class="upload-content">
      <div
        class="upload-area"
        :class="{ 'file-selected': file, 'drag-over': isDragOver }"
        @dragover.prevent="onDragOver"
        @dragleave.prevent="onDragLeave"
        @drop.prevent="onDrop"
      >
        <div class="upload-zone">
          <div class="upload-icon-container">
            <q-icon name="cloud_upload" size="48px" class="upload-icon" />
          </div>

          <div class="upload-text-container">
            <p class="upload-main-text" v-if="!file">
              Arraste e solte sua planilha aqui ou
            </p>
            <p class="upload-main-text selected" v-else>
              <q-icon name="description" class="q-mr-xs" />
              {{ file.name }}
            </p>

            <div class="upload-button">
              <q-btn
                :color="file ? 'positive' : 'primary'"
                :icon="file ? 'check_circle' : 'folder_open'"
                :label="file ? 'Arquivo Selecionado' : 'Selecionar Arquivo'"
                no-caps
                rounded
                class="upload-btn"
                @click="triggerFileInput"
              />
              <input
                ref="fileInput"
                id="arquivo"
                type="file"
                accept=".xlsx,.xls"
                @change="onFileSelected"
                style="display: none"
              />
            </div>
          </div>
        </div>

        <div class="file-info" v-if="file">
          <div class="file-details">
            <q-chip color="primary" text-color="white" icon="info">
              Tamanho: {{ formatFileSize(file.size) }}
            </q-chip>
            <q-chip color="positive" text-color="white" icon="event">
              Tipo: {{ file.type || "Excel" }}
            </q-chip>
          </div>
        </div>
      </div>

      <div class="upload-actions">
        <div class="action-buttons">
          <q-btn
            color="positive"
            label="Enviar Planilha"
            no-caps
            icon="send"
            size="md"
            :disable="!file"
            @click="enviarPlanilha()"
            class="send-button"
          />

          <q-btn
            color="secondary"
            label="Baixar Template"
            no-caps
            icon="download"
            size="md"
            href="https://multpex-docs.s3.amazonaws.com/templates/ferramentas/ferramentas-usuarios.xlsx"
            target="_blank"
            class="template-button"
          />
        </div>
      </div>

      <div class="upload-info">
        <q-card flat bordered class="info-card">
          <q-card-section>
            <div class="info-header">
              <q-icon name="info" color="primary" size="20px" />
              <span class="info-title">Informações Importantes</span>
            </div>
            <ul class="info-list">
              <li>
                <strong>Número do Patrimônio:</strong> Campo obrigatório e único
                no sistema
              </li>
              <li>
                <strong>ID ou SAP#:</strong> Chave única para identificação do
                usuário
              </li>
              <li>
                <strong>Formato:</strong> Apenas arquivos .xlsx são aceitos
              </li>
              <li>
                <strong>Template:</strong> Use o template oficial para evitar
                erros
              </li>
            </ul>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { useQuasar } from "quasar";

export default {
  props: ["titulo"],
  setup() {
    const file = ref(null);
    const isDragOver = ref(false);
    const store = useStore();
    const $q = useQuasar();
    const fileInput = ref(null);

    const onFileSelected = async (e) => {
      if (e.target.files[0]) {
        file.value = e.target.files[0];
      }
    };

    const onDragOver = () => {
      isDragOver.value = true;
    };

    const onDragLeave = () => {
      isDragOver.value = false;
    };

    const onDrop = (e) => {
      isDragOver.value = false;
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && droppedFile.name.endsWith(".xlsx")) {
        file.value = droppedFile;
      } else {
        $q.notify({
          message: "Por favor, selecione apenas arquivos .xlsx",
          color: "negative",
          icon: "warning",
        });
      }
    };

    const formatFileSize = (bytes) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const enviarPlanilha = async () => {
      if (!file.value) {
        $q.notify({
          message: "Selecione uma planilha",
          color: "negative",
          icon: "warning",
          position: "top",
        });
        return;
      }

      if (!file.value.name.endsWith(".xlsx")) {
        $q.notify({
          message: "Apenas arquivos .xlsx são aceitos",
          color: "negative",
          icon: "error",
          position: "top",
        });
        return;
      }

      $q.loading.show({
        message: "Enviando planilha...",
      });

      try {
        const reader = new FileReader();
        reader.readAsDataURL(file.value);
        reader.onload = async function () {
          try {
            await store.dispatch("fer/uploadPlanilhaAssociacaoUsuario", {
              adicional: {
                vendor_id: store.state.org.unidadeData.id,
              },
              mapeamento: "ferramenta-usuario",
              planilha: reader.result,
            });

            $q.loading.hide();
            $q.notify({
              message: "Planilha enviada com sucesso!",
              color: "positive",
              icon: "check_circle",
              position: "bottom",
              timeout: 3000,
            });
            file.value = null;
          } catch (error) {
            $q.loading.hide();
            console.error(error);
            $q.notify({
              message: "Erro ao enviar planilha. Tente novamente.",
              color: "negative",
              icon: "error",
              position: "bottom",
              timeout: 5000,
            });
          }
        };
        reader.onerror = function (error) {
          $q.loading.hide();
          console.log("Error: ", error);
          $q.notify({
            message: "Erro ao ler arquivo",
            color: "negative",
            icon: "error",
          });
        };
      } catch (error) {
        $q.loading.hide();
        console.log(error);
      }
    };

    const resetValue = () => {
      const input = document.getElementById("arquivo");
      if (input) input.value = null;
    };

    const triggerFileInput = () => {
      fileInput.value.click();
    };

    return {
      file,
      isDragOver,
      enviarPlanilha,
      resetValue,
      onFileSelected,
      onDragOver,
      onDragLeave,
      onDrop,
      formatFileSize,
      triggerFileInput,
      fileInput,
    };
  },
};
</script>

<style scoped>
.upload-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 16px;
  padding: 24px;
  margin: 16px 0;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.upload-header {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  gap: 12px;
}

.header-icon {
  color: #1976d2;
}

.upload-title {
  margin: 0;
  color: #2c3e50;
  font-weight: 600;
  font-size: 1.1rem;
}

.upload-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.upload-area {
  background: white;
  border: 2px dashed #e0e4e7;
  border-radius: 12px;
  padding: 32px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.upload-area:hover {
  border-color: #1976d2;
  background: #f8fafe;
  transform: translateY(-2px);
  box-shadow: 0 4px 20px rgba(25, 118, 210, 0.1);
}

.upload-area.file-selected {
  border-color: #4caf50;
  background: #f1f8e9;
}

.upload-area.drag-over {
  border-color: #1976d2;
  background: #e3f2fd;
  transform: scale(1.02);
}

.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.upload-icon-container {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;
}

.upload-icon {
  color: white;
}

.upload-text-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.upload-main-text {
  margin: 0;
  color: #546e7a;
  font-size: 1rem;
  font-weight: 500;
}

.upload-main-text.selected {
  color: #2e7d32;
  font-weight: 600;
}

.upload-button {
  cursor: pointer;
}

.upload-btn {
  min-width: 180px;
  height: 44px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.upload-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.file-info {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e0e4e7;
}

.file-details {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.upload-actions {
  display: flex;
  justify-content: center;
}

.action-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.send-button,
.template-button {
  min-width: 160px;
  height: 44px;
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
}

.send-button:hover,
.template-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.upload-info {
  margin-top: 8px;
}

.info-card {
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
}

.info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.info-title {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.95rem;
}

.info-list {
  margin: 0;
  padding-left: 20px;
  color: #546e7a;
}

.info-list li {
  margin-bottom: 8px;
  font-size: 0.9rem;
  line-height: 1.4;
}

.info-list li:last-child {
  margin-bottom: 0;
}

/* Responsive */
@media (max-width: 600px) {
  .upload-section {
    padding: 16px;
    margin: 12px 0;
  }

  .upload-area {
    padding: 20px 16px;
  }

  .upload-icon-container {
    width: 64px;
    height: 64px;
  }

  .upload-icon {
    font-size: 32px;
  }

  .action-buttons {
    flex-direction: column;
  }

  .send-button,
  .template-button {
    width: 100%;
  }

  .file-details {
    flex-direction: column;
    align-items: center;
  }
}
</style>
